import React, { useState, useEffect, useRef } from 'react';
import { useApi, apiGet, apiPost, apiPut, apiDelete } from '../hooks/useApi';
import { useWebSocket } from '../hooks/useWebSocket';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { Download, Upload, Database, Trash2, RotateCcw, Shield, Monitor, Settings as SettingsIcon, Bell, Palette, Wallet, Receipt, Archive, Clock, ListX, DollarSign, AlertTriangle, Info, Crown } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';
import { AdminConfirmDialog } from '../components/AdminUi';
import AISettings from '../components/AISettings';
import toast from 'react-hot-toast';
import { apiFetch, buildApiUrl } from '../lib/apiClient';
import { API_ROUTES } from '../lib/apiRoutes';
import {
  fetchPublicPlans,
  findPlan,
  formatPlanSummary,
} from '../lib/planCatalog';

function SavedPercentageSlider({ title, description, value, fallback, max, step, onSave, label }) {
  const serverValue = Number.isFinite(Number(value)) ? Number(value) : fallback;
  const [draft, setDraft] = useState(serverValue);
  const [saving, setSaving] = useState(false);
  const draftRef = useRef(serverValue);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!saving) {
      draftRef.current = serverValue;
      setDraft(serverValue);
    }
  }, [serverValue, saving]);

  const updateDraft = (nextValue) => {
    draftRef.current = nextValue;
    setDraft(nextValue);
  };

  const commit = async () => {
    const nextValue = draftRef.current;
    if (savingRef.current || nextValue === serverValue) return;

    savingRef.current = true;
    setSaving(true);
    try {
      const saved = await onSave(nextValue);
      if (!saved) updateDraft(serverValue);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div>
          <span className="text-sm font-medium">{title}</span>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
        <span className="text-sm font-bold text-accent-purple" aria-live="polite">
          {draft}%{saving ? <span className="ml-2 text-xs font-normal text-gray-500">Saving...</span> : null}
        </span>
      </div>
      <input
        type="range"
        min="0"
        max={max}
        step={step}
        value={draft}
        onChange={e => updateDraft(parseInt(e.target.value, 10))}
        onPointerUp={commit}
        onKeyUp={commit}
        onBlur={commit}
        disabled={saving}
        aria-label={label}
        className="w-full accent-accent-purple disabled:opacity-60"
      />
    </div>
  );
}
export default function Settings() {
  const { isConnected } = useWebSocket();
  const { theme, setTheme, themes } = useTheme();
  const { user } = useAuth();
  const { isPro, isFree, isTrial, isTrialActive, trialDaysRemaining, effectivePlan } = useSubscription();
  const { data: settings, loading, refetch } = useApi('/settings');
  const { data: dashboardSummary, refetch: refetchDashboardSummary } = useApi('/dashboard/summary');
  const [backups, setBackups] = useState([]);
  const [restoreConfirm, setRestoreConfirm] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [dangerAction, setDangerAction] = useState(null);
  const [plans, setPlans] = useState([]);
  const [apiStatus, setApiStatus] = useState('checking');

  useEffect(() => {
    if (settings?.backups_available) {
      setBackups(settings.backups_available);
    }
  }, [settings]);

  useEffect(() => {
    fetchPublicPlans()
      .then(setPlans)
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;

    const checkApiHealth = async () => {
      try {
        const res = await apiFetch(API_ROUTES.public.health, {
          auth: false,
          cache: 'no-store',
        });
        if (!cancelled) setApiStatus(res.ok ? 'connected' : 'disconnected');
      } catch (e) {
        if (!cancelled) setApiStatus('disconnected');
      }
    };

    checkApiHealth();
    const timer = setInterval(checkApiHealth, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(refetchDashboardSummary, 60000);
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refetchDashboardSummary();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [refetchDashboardSummary]);
  const freePlan = findPlan(plans, 'free');
  const proPlan = findPlan(plans, 'pro');
  const activePlan = isTrial
    ? (proPlan || findPlan(plans, effectivePlan))
    : (findPlan(plans, effectivePlan) || (isPro ? proPlan : freePlan));
  const subscriptionSummary = isTrial
    ? `Trial access currently mirrors PRO: ${formatPlanSummary(activePlan)}`
    : formatPlanSummary(activePlan);
  const apiConnected = apiStatus === 'connected';
  const syncHealth = dashboardSummary?.sync_health || {};
  const eaDataState = syncHealth.status === 'live' ? 'live' : syncHealth.status === 'idle' || syncHealth.status === 'partial' ? 'idle' : 'offline';
  const eaDataLabel = eaDataState === 'live'
    ? 'Live - EA data received recently'
    : eaDataState === 'idle'
      ? 'Idle - no new data recently'
      : 'Offline - no EA data for a while';
  const eaDataClass = eaDataState === 'live'
    ? 'bg-accent-green animate-pulse'
    : eaDataState === 'idle'
      ? 'bg-accent-orange animate-pulse'
      : 'bg-accent-red';
  const apiLabel = apiStatus === 'checking'
    ? 'Checking backend API...'
    : apiConnected
      ? 'API connected'
      : 'API unavailable';
  const wsLabel = isConnected ? 'WebSocket connected' : 'WebSocket standby';

  const handleBackup = async () => {
    try {
      await apiPost('/settings/backup', {});
      toast.success('Backup created successfully');
      refetch();
    } catch (e) {
      toast.error('Backup failed');
    }
  };

  const handleRestore = async () => {
    if (!restoreConfirm) return;
    try {
      await apiPost(`/settings/restore?filename=${restoreConfirm.filename}`, {});
      toast.success('Restored! Restart backend to apply.');
      setRestoreConfirm(null);
    } catch (e) {
      toast.error('Restore failed');
    }
  };

  const handleDeleteBackup = async () => {
    if (!deleteConfirm) return;
    try {
      await apiDelete(`/settings/backups/${deleteConfirm.filename}`);
      toast.success('Backup deleted');
      setDeleteConfirm(null);
      refetch();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleExport = () => {
    window.open(buildApiUrl('/api/settings/export'), '_blank');
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await apiFetch('/api/settings/import', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      toast.success(data.message || 'Import successful');
    } catch (err) {
      toast.error('Import failed');
    }
  };

  const handleSettingsChange = async (updates) => {
    try {
      await apiPut('/settings', updates);
      await refetch();
      return true;
    } catch (e) {
      toast.error('Failed to update setting');
      return false;
    }
  };

  const handleSettingChange = (key, value) => handleSettingsChange({ [key]: value });

  const runDangerAction = async () => {
    if (!dangerAction) return;
    await handleSettingChange(dangerAction.key, true);
    toast.success(dangerAction.successMessage);
    setDangerAction(null);
    refetch();
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Settings</h1>
        <p className="text-sm text-gray-400">Configure your TrackMyFxEA</p>
      </div>

      {/* Connection Status */}
      <div className="card">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
          <Shield size={16} className="text-accent-green" /> Connection Status
        </h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${eaDataClass}`} />
            <div>
              <span className="text-sm font-semibold">EA Data: {eaDataLabel}</span>
              <p className="text-xs text-gray-500">
                {syncHealth.synced_accounts || 0}/{syncHealth.visible_accounts || 0} accounts recently connected
                {syncHealth.market_closed ? ' - forex market closed' : ''}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-400">
            <div className="flex items-center gap-2 bg-dark-bg border border-dark-border rounded-lg px-3 py-2">
              <span className={`w-2 h-2 rounded-full ${apiStatus === 'checking' ? 'bg-accent-orange' : apiConnected ? 'bg-accent-green' : 'bg-accent-red'}`} />
              <span>{apiLabel}</span>
            </div>
            <div className="flex items-center gap-2 bg-dark-bg border border-dark-border rounded-lg px-3 py-2">
              <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-accent-green' : 'bg-accent-orange'}`} />
              <span>{wsLabel}</span>
            </div>
          </div>
        </div>
        <div className="mt-3 text-xs text-gray-500 space-y-1">
          <p>API: {buildApiUrl('/api')}</p>
          <p>WebSocket: {buildApiUrl('/ws/live-updates').replace(/^http/, 'ws')}</p>
        </div>
      </div>
      {/* Subscription Plan */}
      <div className="card">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
          <Crown size={16} className="text-accent-purple" /> Subscription Plan
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                isPro ? 'bg-accent-purple/20 text-accent-purple' :
                isTrial ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-accent-green/20 text-accent-green'
              }`}>
                {effectivePlan.toUpperCase()}
              </span>
              <span className="text-sm text-gray-300">{user?.email}</span>
            </div>
            {isTrialActive && (
              <p className="text-xs text-yellow-400 mb-1">
                Trial ends in {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} - all PRO features unlocked.
              </p>
            )}
            <p className="text-xs text-gray-500">
              {subscriptionSummary}
            </p>
          </div>
          {(isFree || isTrial) && (
            <a
              href="https://trackmyfxea.com/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg text-sm font-medium text-white"
              style={{ background: 'linear-gradient(135deg, #a855f7, #6366f1)' }}
            >
              {isTrial ? 'Upgrade Now' : 'Upgrade to PRO'}
            </a>
          )}
          {isPro && (
            <span className="text-xs text-gray-500">Manage at trackmyfxea.com/account</span>
          )}
        </div>
      </div>

      {/* Display Settings */}
      <div className="card">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
          <Monitor size={16} className="text-accent-green" /> Display Settings
        </h3>
        <div className="space-y-5">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm font-medium">Auto-Refresh Dashboard</span>
              <p className="text-xs text-gray-500">Automatically refresh data on the dashboard</p>
            </div>
            <button
              onClick={() => handleSettingChange('auto_refresh', !(settings?.auto_refresh !== false))}
              className={`w-12 h-6 rounded-full transition-colors ${settings?.auto_refresh !== false ? 'bg-accent-purple' : 'bg-dark-border'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full transition-transform ${settings?.auto_refresh !== false ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm font-medium">Refresh Interval</span>
              <p className="text-xs text-gray-500">How often to refresh dashboard data (seconds)</p>
            </div>
            <select
              value={settings?.refresh_interval || 60}
              onChange={e => handleSettingChange('refresh_interval', parseInt(e.target.value))}
              className="bg-dark-bg border border-dark-border rounded-lg px-3 py-1.5 text-sm text-gray-300"
            >
              <option value={15}>15 seconds</option>
              <option value={30}>30 seconds</option>
              <option value={60}>1 minute</option>
              <option value={120}>2 minutes</option>
              <option value={300}>5 minutes</option>
            </select>
          </div>
        </div>
      </div>

      {/* General Settings */}
      <div className="card">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><SettingsIcon size={16} className="text-accent-green" /> General</h3>
        <div className="space-y-4 animate-stagger">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm">Refresh Interval</span>
              <p className="text-xs text-gray-500">How often the EA exports data</p>
            </div>
            <span className="text-sm text-gray-300">{settings?.refresh_interval || 60}s</span>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm">Broker Server Timezone</span>
              <p className="text-xs text-gray-500">Your broker's server time (used for hour/day analysis)</p>
            </div>
            <select
              value={settings?.broker_timezone || 'UTC-5'}
              onChange={e => handleSettingChange('broker_timezone', e.target.value)}
              className="bg-dark-bg border border-dark-border rounded-lg px-3 py-1.5 text-sm text-gray-300"
            >
              <option value="UTC-5">New York (UTC-5 Winter / UTC-4 Summer)</option>
              <option value="UTC+0">London (UTC+0 / UTC+1 Summer)</option>
              <option value="UTC+2">EET (UTC+2 / UTC+3 Summer)</option>
              <option value="UTC+3">Moscow (UTC+3)</option>
              <option value="UTC+8">Singapore (UTC+8)</option>
              <option value="UTC+9">Tokyo (UTC+9)</option>
              <option value="UTC">UTC (No offset)</option>
            </select>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm">Currency Format</span>
            </div>
            <span className="text-sm text-gray-300">{settings?.currency_format || 'USD'}</span>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="card">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><Bell size={16} className="text-accent-green" /> Notifications</h3>
        <div className="space-y-4 animate-stagger">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm">Desktop Notifications</span>
              <p className="text-xs text-gray-500">Windows toast alerts for drawdown & trades</p>
            </div>
            <button
              onClick={() => handleSettingChange('notifications_enabled', !(settings?.notifications_enabled))}
              className={`w-12 h-6 rounded-full transition-colors ${settings?.notifications_enabled ? 'bg-accent-green' : 'bg-dark-border'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full transition-transform ${settings?.notifications_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm">Trade Alerts</span>
              <p className="text-xs text-gray-500">Notify on trade open/close</p>
            </div>
            <button
              onClick={() => handleSettingChange('trade_alerts', !(settings?.trade_alerts))}
              className={`w-12 h-6 rounded-full transition-colors ${settings?.trade_alerts ? 'bg-accent-green' : 'bg-dark-border'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full transition-transform ${settings?.trade_alerts ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm">Drawdown Alert Threshold</span>
              <p className="text-xs text-gray-500">Alert when account DD exceeds this %</p>
            </div>
            <select
              value={settings?.drawdown_alert_pct || 10}
              onChange={e => handleSettingChange('drawdown_alert_pct', parseFloat(e.target.value))}
              className="bg-dark-bg border border-dark-border rounded-lg px-3 py-1.5 text-sm text-accent-orange"
            >
              <option value={5}>5%</option>
              <option value={10}>10%</option>
              <option value={15}>15%</option>
              <option value={20}>20%</option>
              <option value={25}>25%</option>
              <option value={30}>30%</option>
              <option value={40}>40%</option>
              <option value={50}>50%</option>
            </select>
          </div>
        </div>
      </div>

      {/* Theme Selector - Premium */}
      <div className="card">
        <h3 className="text-sm font-semibold mb-5 flex items-center gap-2">
          <Palette size={16} style={{ color: 'var(--accent-primary)' }} /> Appearance
        </h3>
        <p className="text-xs text-[var(--text-dim)] mb-4">Choose a theme to personalize your experience</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(themes).map(([key, t]) => (
            <button
              key={key}
              onClick={() => setTheme(key)}
              className={`group relative p-4 rounded-2xl border-2 transition-all duration-300 text-left overflow-hidden ${
                theme === key
                  ? 'scale-[1.02]'
                  : 'hover:scale-[1.01] hover:border-white/15'
              }`}
              style={{
                background: t.preview[0],
                borderColor: theme === key ? t.preview[2] : 'rgba(255,255,255,0.08)',
                boxShadow: theme === key ? `0 0 30px ${t.preview[2]}40, 0 4px 16px rgba(0,0,0,0.3)` : '0 2px 8px rgba(0,0,0,0.2)',
              }}
            >
              {/* Active indicator glow */}
              {theme === key && (
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full blur-2xl opacity-40 pointer-events-none" style={{ background: t.preview[2] }} />
              )}
              {/* Active check */}
              {theme === key && (
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: t.preview[2] }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5L4 7L8 3" stroke={t.preview[0]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
              )}
              {/* Color swatches */}
              <div className="flex gap-1.5 mb-3 relative">
                {t.preview.map((color, i) => (
                  <div key={i} className="w-5 h-5 rounded-full border border-white/15 transition-transform group-hover:scale-110" style={{ background: color, boxShadow: theme === key ? `0 0 8px ${color}60` : 'none' }} />
                ))}
              </div>
              <p className="text-xs font-bold relative" style={{ color: t.preview[2] }}>{t.name}</p>
              <p className="text-[10px] mt-0.5 relative" style={{ color: 'rgba(255,255,255,0.45)' }}>{t.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Withdrawal Settings */}
      <div className="card">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><Wallet size={16} className="text-accent-green" /> Withdrawal Settings</h3>
        <div className="space-y-5">
          <SavedPercentageSlider
            title="Weekly Withdrawal Percentage"
            description="Percentage of weekly profits to withdraw"
            value={settings?.withdrawal_pct_weekly}
            fallback={30}
            max={100}
            step={5}
            label="Weekly withdrawal percentage"
            onSave={value => handleSettingChange('withdrawal_pct_weekly', value)}
          />
          <SavedPercentageSlider
            title="Monthly Withdrawal Percentage"
            description="Percentage of monthly profits to withdraw"
            value={settings?.withdrawal_pct_monthly}
            fallback={30}
            max={100}
            step={5}
            label="Monthly withdrawal percentage"
            onSave={value => handleSettingChange('withdrawal_pct_monthly', value)}
          />
          <div className="bg-dark-bg border border-dark-border rounded-lg p-3">
            <p className="text-xs text-accent-purple font-medium mb-2">Quick Presets: <span className="text-gray-400 font-normal">Choose a withdrawal strategy</span></p>
            <div className="flex gap-2">
              <button onClick={() => handleSettingsChange({ withdrawal_pct_weekly: 20, withdrawal_pct_monthly: 20 })} className="px-3 py-1.5 bg-accent-purple/10 border border-accent-purple/30 rounded-lg text-xs text-accent-purple">Conservative (20%)</button>
              <button onClick={() => handleSettingsChange({ withdrawal_pct_weekly: 30, withdrawal_pct_monthly: 30 })} className="px-3 py-1.5 bg-accent-purple/10 border border-accent-purple/30 rounded-lg text-xs text-accent-purple">Moderate (30%)</button>
              <button onClick={() => handleSettingsChange({ withdrawal_pct_weekly: 50, withdrawal_pct_monthly: 50 })} className="px-3 py-1.5 bg-accent-purple/10 border border-accent-purple/30 rounded-lg text-xs text-accent-purple">Aggressive (50%)</button>
            </div>
          </div>
        </div>
      </div>
      {/* Tax Settings */}
      <div className="card">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><Receipt size={16} className="text-accent-green" /> Tax Settings</h3>
        <div className="space-y-5">
          <SavedPercentageSlider
            title="Tax Rate"
            description="Your estimated tax rate on trading profits"
            value={settings?.tax_rate}
            fallback={25}
            max={50}
            step={1}
            label="Estimated tax rate"
            onSave={value => handleSettingChange('tax_rate', value)}
          />
          <div className="bg-dark-bg border border-dark-border rounded-lg p-3">
            <p className="text-xs text-accent-purple font-medium mb-2">Common Tax Rates:</p>
            <div className="flex gap-2 flex-wrap">
              {[{v:0,l:'0% (Tax-free)'},{v:15,l:'15% (Long-term)'},{v:22,l:'22% (Standard)'},{v:25,l:'25%'},{v:30,l:'30%'},{v:37,l:'37% (High bracket)'}].map(t => (
                <button key={t.v} onClick={() => handleSettingChange('tax_rate', t.v)} className="px-3 py-1 bg-dark-card border border-dark-border rounded text-xs text-gray-300 hover:border-accent-purple">{t.l}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Data Retention */}
      <div className="card">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><Clock size={16} className="text-accent-green" /> Data Retention</h3>
        <div className="space-y-4 animate-stagger">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm font-medium">Keep Trade History</span>
              <p className="text-xs text-gray-500">Older trades will be archived automatically</p>
            </div>
            <select
              value={settings?.data_retention || 'forever'}
              onChange={e => handleSettingChange('data_retention', e.target.value)}
              className="bg-dark-bg border border-dark-border rounded-lg px-3 py-1.5 text-sm text-gray-300"
            >
              <option value="30d">30 Days</option>
              <option value="90d">90 Days</option>
              <option value="180d">6 Months</option>
              <option value="1y">1 Year</option>
              <option value="forever">Forever</option>
            </select>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm font-medium">Keep Equity Snapshots</span>
              <p className="text-xs text-gray-500">Historical equity curve data points</p>
            </div>
            <select
              value={settings?.equity_retention || 'forever'}
              onChange={e => handleSettingChange('equity_retention', e.target.value)}
              className="bg-dark-bg border border-dark-border rounded-lg px-3 py-1.5 text-sm text-gray-300"
            >
              <option value="90d">90 Days</option>
              <option value="1y">1 Year</option>
              <option value="forever">Forever</option>
            </select>
          </div>
        </div>
      </div>

      {/* EA Ignore List */}
      <div className="card">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><ListX size={16} className="text-accent-green" /> EA Ignore List</h3>
        <p className="text-xs text-gray-500 mb-3">Magic numbers listed here will be excluded from all tracking and reports.</p>
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Enter magic number to ignore..."
              id="ignore-magic-input"
              className="flex-1 bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-300"
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const val = e.target.value.trim();
                  if (val && !isNaN(val)) {
                    const current = settings?.ignored_magics || [];
                    if (!current.includes(parseInt(val))) {
                      handleSettingChange('ignored_magics', [...current, parseInt(val)]);
                      e.target.value = '';
                    }
                  }
                }
              }}
            />
            <button
              onClick={() => {
                const input = document.getElementById('ignore-magic-input');
                const val = input?.value?.trim();
                if (val && !isNaN(val)) {
                  const current = settings?.ignored_magics || [];
                  if (!current.includes(parseInt(val))) {
                    handleSettingChange('ignored_magics', [...current, parseInt(val)]);
                    input.value = '';
                  }
                }
              }}
              className="px-4 py-2 bg-accent-red/20 border border-accent-red/30 rounded-lg text-sm text-accent-red font-medium hover:bg-accent-red/30"
            >
              Add
            </button>
          </div>
          {(settings?.ignored_magics || []).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {(settings?.ignored_magics || []).map(magic => (
                <span key={magic} className="flex items-center gap-2 px-3 py-1 bg-dark-bg border border-dark-border rounded-lg text-xs">
                  <span className="font-mono text-accent-red">{magic}</span>
                  <button
                    onClick={() => handleSettingChange('ignored_magics', (settings?.ignored_magics || []).filter(m => m !== magic))}
                    className="text-gray-500 hover:text-accent-red"
                  >x</button>
                </span>
              ))}
            </div>
          )}
          {(settings?.ignored_magics || []).length === 0 && (
            <p className="text-xs text-gray-500 italic">No magic numbers ignored. All EAs are being tracked.</p>
          )}
        </div>
      </div>

      {/* Currency Display */}
      <div className="card">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><DollarSign size={16} className="text-accent-green" /> Currency Display</h3>
        <div className="space-y-4 animate-stagger">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm font-medium">Display Currency</span>
              <p className="text-xs text-gray-500">Currency symbol shown throughout the dashboard</p>
            </div>
            <select
              value={settings?.display_currency || 'USD'}
              onChange={e => handleSettingChange('display_currency', e.target.value)}
              className="bg-dark-bg border border-dark-border rounded-lg px-3 py-1.5 text-sm text-gray-300"
            >
              <option value="USD">$ USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="JPY">JPY</option>
              <option value="AUD">AUD</option>
              <option value="CAD">CAD</option>
              <option value="CHF">CHF</option>
              <option value="account">Account Currency</option>
            </select>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm font-medium">Decimal Places</span>
              <p className="text-xs text-gray-500">Number of decimal places for profit values</p>
            </div>
            <select
              value={settings?.decimal_places || 2}
              onChange={e => handleSettingChange('decimal_places', parseInt(e.target.value))}
              className="bg-dark-bg border border-dark-border rounded-lg px-3 py-1.5 text-sm text-gray-300"
            >
              <option value={0}>0 ($1,234)</option>
              <option value={2}>2 ($1,234.56)</option>
              <option value={3}>3 ($1,234.567)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reset Options */}
      <div className="card border-accent-red/20">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 text-accent-red"><AlertTriangle size={16} /> Danger Zone</h3>
        <div className="space-y-4 animate-stagger">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm font-medium">Reset Settings</span>
              <p className="text-xs text-gray-500">Reset all settings to default values. Data is preserved.</p>
            </div>
            <button
              onClick={() => setDangerAction({ key: 'reset_settings', title: 'Reset Settings', message: 'Reset all settings to defaults? Your trade data will be preserved.', confirmLabel: 'Reset Settings', successMessage: 'Settings reset to defaults', tone: 'warning' })}
              className="px-4 py-1.5 bg-dark-bg border border-dark-border rounded-lg text-xs text-gray-300 hover:border-accent-orange/50"
            >
              Reset Settings
            </button>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm font-medium text-accent-red">Clear All Trade Data</span>
              <p className="text-xs text-gray-500">Delete all trades, EA stats, and history. Cannot be undone.</p>
            </div>
            <button
              onClick={() => setDangerAction({ key: 'clear_all_data', title: 'Clear All Trade Data', message: 'Delete all trades, EA stats, drawdown records, and history for this user. This cannot be undone.', confirmLabel: 'Clear All Data', successMessage: 'All data cleared', requireText: 'DELETE' })}
              className="px-4 py-1.5 bg-accent-red/20 border border-accent-red/30 rounded-lg text-xs text-accent-red font-medium hover:bg-accent-red/30"
            >
              Clear All Data
            </button>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm font-medium text-accent-red">Factory Reset</span>
              <p className="text-xs text-gray-500">Delete everything - settings, data, backups. Fresh start.</p>
            </div>
            <button
              onClick={() => setDangerAction({ key: 'factory_reset', title: 'Factory Reset', message: 'Delete everything for this user, including settings, trading data, and backups. This cannot be undone.', confirmLabel: 'Factory Reset', successMessage: 'Factory reset complete', requireText: 'FACTORY RESET' })}
              className="px-4 py-1.5 bg-accent-red border-none rounded-lg text-xs text-white font-bold hover:bg-red-600"
            >
              Factory Reset
            </button>
          </div>
        </div>
      </div>

      {/* AI Configuration */}
      <AISettings />

      {/* About */}
      <div className="card">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><Info size={16} className="text-accent-green" /> About</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <p><span className="text-white">TrackMyFxEA</span> v1.0</p>
          <p>Local desktop trading dashboard for MT4/MT5</p>
          <p className="text-xs text-gray-600 mt-3">
            Stack: MQL4/5 EA ? JSON ? Python/FastAPI/SQLite ? React/TailwindCSS/Recharts
          </p>
          <p className="text-xs text-gray-600">
            Features: Real-time sync, auto-grouping, drawdown alerts, withdrawal calculator, backup system
          </p>
        </div>
      </div>

      <AdminConfirmDialog
        open={!!dangerAction}
        title={dangerAction?.title}
        message={dangerAction?.message}
        confirmLabel={dangerAction?.confirmLabel}
        tone={dangerAction?.tone || 'danger'}
        requireText={dangerAction?.requireText || ''}
        onConfirm={runDangerAction}
        onCancel={() => setDangerAction(null)}
      />

      {/* Confirm Dialogs */}
      <ConfirmDialog
        open={!!restoreConfirm}
        title="Restore Backup"
        message={`Restore from ${restoreConfirm?.filename}? Current data will be backed up first. You'll need to restart the backend after restore.`}
        onConfirm={handleRestore}
        onCancel={() => setRestoreConfirm(null)}
      />
      <ConfirmDialog
        open={!!deleteConfirm}
        title="Delete Backup"
        message={`Delete ${deleteConfirm?.filename}? This cannot be undone.`}
        onConfirm={handleDeleteBackup}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
}



